import { CSSProperties, useState } from 'react';
import { CategoryBadge } from './CategoryBadge';
import { StarRating } from './StarRating';
import { gradientFor } from './imageGradients';
import { PhotoCarousel, type PhotoCarouselPhoto } from './PhotoCarousel';

/**
 * HighlightCard — the hero itinerary card: a full-width image/gradient header
 * with a serif title (+ optional CategoryBadge)
 * and a tip below. Vertical counterpart to StopCard, for the standout stop of
 * a day. Hover lifts, warms the hairline to --accent, and eases the image in.
 *
 * Header: pass `photoUrl` for a real image, or `gradientKey` (food, drink,
 * view, nature, landmark, art, shop, travel) to pull a base colour + icon from
 * the canonical palette. `gradient`/`icon` override the resolved values.
 */
export interface HighlightCardProps {
  title: string;
  /** Short tip/description under the title. */
  tip?: string;
  /** Alias of `tip` (repo call-site compat) — used when `tip` is omitted. */
  description?: string;
  /** Inline category pill, e.g. "Food & Drink". */
  category?: string;
  /** Palette key for the header fallback: food | drink | view | nature | landmark | art | shop | travel. Default 'food'. */
  gradientKey?: 'food' | 'drink' | 'view' | 'nature' | 'landmark' | 'art' | 'shop' | 'travel';
  /** Custom CSS background overriding the palette base colour. */
  gradient?: string;
  /** Tabler icon name (no `ti-` prefix) overriding the palette icon. */
  icon?: string;
  /** Photo URL — replaces the gradient when present. */
  photoUrl?: string | null;
  /** Multiple photos → swipeable carousel. Takes precedence over photoUrl. */
  photos?: PhotoCarouselPhoto[];
  /** Optional focal point (0–100) for object-position when a photo is shown. */
  photoFocal?: { x: number; y: number } | null;
  /** Header image height in px. Default 248 (per the Handbook screen). */
  imageHeight?: number;
  /** The traveler's own rating (1–5): shows a small read-only ★★★★☆ under the text. */
  ownRating?: number;
  onClick?: () => void;
  style?: CSSProperties;
}

export function HighlightCard({
  title, tip, description, category,
  gradientKey = 'food', gradient, icon, photoUrl, photos, photoFocal, imageHeight = 248, onClick, style, ownRating,
}: HighlightCardProps) {
  const [hover, setHover] = useState(false);
  const g = gradientFor(gradientKey);
  const bg = gradient || g.base;
  const carouselPhotos = photos ?? [];
  const glyph = icon || g.icon;
  const bodyText = tip ?? description;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        border: `1px solid ${hover ? 'var(--accent-border)' : 'var(--line)'}`,
        boxShadow: hover ? 'var(--shadow-hover)' : 'none',
        borderRadius: 'var(--r-card)',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transform: hover ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'border-color .3s cubic-bezier(.16,.8,.3,1), transform .3s cubic-bezier(.16,.8,.3,1), box-shadow .3s ease',
        background: 'var(--bg)',
        ...style,
      }}
    >
      <div style={{ position: 'relative', height: imageHeight, overflow: 'hidden' }}>
        {carouselPhotos.length > 1 ? (
          <PhotoCarousel photos={carouselPhotos} alt={title} />
        ) : photoUrl || carouselPhotos.length === 1 ? (
          <img
            src={carouselPhotos[0]?.url ?? photoUrl ?? ''}
            alt={title}
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
              objectPosition: (() => { const f = carouselPhotos[0]?.focal ?? photoFocal; return f ? `${f.x}% ${f.y}%` : undefined; })(),
              transform: hover ? 'scale(1.06)' : 'scale(1)', transition: 'transform .5s cubic-bezier(.16,.8,.3,1)',
            }}
          />
        ) : (
          <div style={{ position: 'absolute', inset: 0, background: bg, transform: hover ? 'scale(1.06)' : 'scale(1)', transition: 'transform .5s cubic-bezier(.16,.8,.3,1)' }}>
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(110% 95% at 28% 18%, rgba(255,255,255,.12), transparent 60%)' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(104deg, rgba(255,255,255,.04) 0 2px, transparent 2px 30px)' }} />
            <i className={`ti ti-${glyph}`} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 44, color: 'rgb(var(--cream) / .22)' }} />
          </div>
        )}
      </div>

      <div style={{ padding: 'var(--sp-highlight-pad-y) var(--sp-highlight-pad-x)' }}>
        <div style={{ lineHeight: 'var(--lh-card-title)' }}>
          <span style={{ fontFamily: 'var(--font-serif)', fontWeight: 400, fontSize: 'var(--fs-card-title-lg)', color: 'var(--text)', verticalAlign: 'middle' }}>{title}</span>
          {category ? <span style={{ marginLeft: 9 }}><CategoryBadge label={category} /></span> : null}
        </div>
        {bodyText ? (
          <p style={{ fontFamily: 'var(--font-sans)', color: 'var(--text-soft)', fontSize: 'var(--fs-body)', lineHeight: 'var(--lh-body)', marginTop: 10, textWrap: 'pretty' }}>{bodyText}</p>
        ) : null}
        {ownRating ? <StarRating value={ownRating} style={{ marginTop: 8 }} /> : null}
      </div>
    </div>
  );
}

export default HighlightCard;
